import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { addMonths, format, parseISO, setMonth, setYear, startOfDay, subMonths } from 'date-fns';

import iconCaret from '../../assets/icon-caret.svg';
import iconChevron from '../../assets/icon-chevron-prev.svg';
import iconSelectCaret from '../../assets/icon-select-caret.svg';

import { HEBREW_MONTHS, texts, WEEKDAY_LABELS } from './constants';
import {
  buildYearOptions,
  getCalendarDays,
  isDayOutsideMonth,
  isDaySelectable,
  isDaySelected,
  isDayToday,
} from './utils';
import css from './BirthDatePicker.module.scss';

type DropdownType = 'month' | 'year' | null;

type BirthDatePickerProps = {
  value: string;
  onOpen: (isOpen: boolean) => void;
  onChange: (value: string) => void;
};

const formatDisplayDate = (value: string) => format(parseISO(value), 'dd/MM/yyyy');

export const BirthDatePicker = ({ value, onOpen, onChange }: BirthDatePickerProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const today = useMemo(() => startOfDay(new Date()), []);
  const maxYear = today.getFullYear();

  const parsedValue = value ? parseISO(value) : null;

  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => parsedValue ?? today);
  const [openDropdown, setOpenDropdown] = useState<DropdownType>(null);

  const calendarDays = useMemo(() => getCalendarDays(viewDate), [viewDate]);
  const yearOptions = useMemo(() => buildYearOptions(maxYear), [maxYear]);

  useEffect(() => {
    onOpen(isOpen);
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setOpenDropdown(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onOpen]);

  const handleDaySelect = (day: Date) => {
    if (!isDaySelectable(day, today)) return;

    onChange(format(day, 'yyyy-MM-dd'));
    setViewDate(day);
    setIsOpen(false);
    setOpenDropdown(null);
  };

  const handlePrevMonth = () => {
    setViewDate((current) => subMonths(current, 1));
    setOpenDropdown(null);
  };

  const handleNextMonth = () => {
    setViewDate((current) => addMonths(current, 1));
    setOpenDropdown(null);
  };

  const toggleDropdown = (type: DropdownType) => {
    setOpenDropdown((current) => (current === type ? null : type));
  };

  return (
    <div ref={rootRef} className={css.root}>
      <button
        type="button"
        className={css.trigger}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => {
          setIsOpen((open) => !open);
          setOpenDropdown(null);
        }}
      >
        <img className={css.triggerCaret} src={iconCaret} alt="" width={9} height={7} aria-hidden />
        <span className={css.triggerLabel}>{value ? formatDisplayDate(value) : texts.placeholder}</span>
      </button>

      {isOpen && (
        <div className={css.panel} role="dialog" aria-label={texts.placeholder}>
          <div className={css.nav}>
            <button
              type="button"
              className={cn(css.navArrow, css.navArrowPrev)}
              aria-label={texts.prevMonth}
              onClick={handlePrevMonth}
            >
              <img src={iconChevron} alt="" width={13} height={31} />
            </button>

            <div className={css.selects}>
              <div className={cn(css.selectWrap, openDropdown === 'year' && css.selectWrapOpen)}>
                <button
                  type="button"
                  className={css.selectTrigger}
                  aria-expanded={openDropdown === 'year'}
                  aria-controls={`${listboxId}-year`}
                  onClick={() => toggleDropdown('year')}
                >
                  <img className={css.selectCaret} src={iconSelectCaret} alt="" width={8} height={4} aria-hidden />
                  <span>{viewDate.getFullYear()}</span>
                </button>
                {openDropdown === 'year' && (
                  <ul id={`${listboxId}-year`} className={css.selectList} role="listbox">
                    {yearOptions.map((year) => (
                      <li key={year} role="none">
                        <button
                          type="button"
                          role="option"
                          aria-selected={year === viewDate.getFullYear()}
                          className={cn(css.selectOption, year === viewDate.getFullYear() && css.selectOptionActive)}
                          onClick={() => {
                            setViewDate((current) => setYear(current, year));
                            setOpenDropdown(null);
                          }}
                        >
                          {year}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className={cn(css.selectWrap, openDropdown === 'month' && css.selectWrapOpen)}>
                <button
                  type="button"
                  className={css.selectTrigger}
                  aria-expanded={openDropdown === 'month'}
                  aria-controls={`${listboxId}-month`}
                  onClick={() => toggleDropdown('month')}
                >
                  <img className={css.selectCaret} src={iconSelectCaret} alt="" width={8} height={4} aria-hidden />
                  <span>{HEBREW_MONTHS[viewDate.getMonth()]}</span>
                </button>
                {openDropdown === 'month' && (
                  <ul id={`${listboxId}-month`} className={css.selectList} role="listbox">
                    {HEBREW_MONTHS.map((month, index) => (
                      <li key={month} role="none">
                        <button
                          type="button"
                          role="option"
                          aria-selected={index === viewDate.getMonth()}
                          className={cn(css.selectOption, index === viewDate.getMonth() && css.selectOptionActive)}
                          onClick={() => {
                            setViewDate((current) => setMonth(current, index));
                            setOpenDropdown(null);
                          }}
                        >
                          {month}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <button
              type="button"
              className={cn(css.navArrow, css.navArrowNext)}
              aria-label={texts.nextMonth}
              onClick={handleNextMonth}
            >
              <img src={iconChevron} alt="" width={13} height={31} />
            </button>
          </div>

          <div className={css.calendar}>
            <div className={css.weekdays}>
              {WEEKDAY_LABELS.map((label) => (
                <span key={label} className={css.weekday}>
                  {label}
                </span>
              ))}
            </div>

            <div className={css.days}>
              {calendarDays.map((day) => {
                const outside = isDayOutsideMonth(day, viewDate);
                const selectable = isDaySelectable(day, today);
                const selected = isDaySelected(day, parsedValue);
                const isToday = isDayToday(day, today);

                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    className={cn(
                      css.day,
                      outside && css.dayOutside,
                      isToday && css.dayToday,
                      selected && css.daySelected,
                      !selectable && css.dayDisabled,
                    )}
                    disabled={!selectable || outside}
                    onClick={() => handleDaySelect(day)}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
